import React, { useEffect, useState, useRef } from 'react';
import { calculateCoverageWithBackground } from '../utils/coverageCalculator';
import { Layer } from '../types';

interface CoverageDisplayProps {
  svgContent: string;
  width: number;
  height: number;
  backgroundImage: string | null;
  updateRate?: number; // 更新間隔（ミリ秒）
  layers?: Layer[]; // レイヤー情報を追加
}

const CoverageDisplay: React.FC<CoverageDisplayProps> = ({
  svgContent,
  width,
  height,
  backgroundImage,
  updateRate = 2000, // デフォルトで2秒ごとに更新
  layers = [], // デフォルト値として空配列を設定
}) => {
  const [coverage, setCoverage] = useState<number>(0);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [threshold] = useState<number>(1);

  // 前回の計算パラメータを保存
  // レイヤーの色情報も保存
  const lastParamsRef = useRef({
    svgContent: '',
    width: 0,
    height: 0,
    backgroundImage: '',
    threshold: 0,
    layerColors: '', // 追加: レイヤーの色情報
  });

  // 計算のスキップフラグ
  const [skipCalculation, setSkipCalculation] = useState<boolean>(false);

  // 計算実行タイマーID
  const timerRef = useRef<number | null>(null);

  // svgContentが変更されたことを追跡するための依存関係
  const svgContentRef = useRef<string>('');
  useEffect(() => {
    // SVG内容が変更された場合は再計算フラグをセット
    if (svgContent !== svgContentRef.current) {
      setSkipCalculation(false);
      svgContentRef.current = svgContent;
    }
  }, [svgContent]);

  useEffect(() => {
    // 背景画像がない場合は計算しない
    if (!backgroundImage) {
      setCoverage(0);
      setSkipCalculation(true);
      return;
    }

    // レイヤーの色情報を文字列化
    const layerColors = layers
      .filter((layer) => layer.visible && layer.svgPattern)
      .map((layer) => layer.parameters.color)
      .join(',');

    const paramsChanged =
      lastParamsRef.current.svgContent !== svgContent ||
      lastParamsRef.current.width !== width ||
      lastParamsRef.current.height !== height ||
      lastParamsRef.current.backgroundImage !== backgroundImage ||
      lastParamsRef.current.threshold !== threshold ||
      lastParamsRef.current.layerColors !== layerColors;

    // パラメータが変わらなければ計算をスキップ
    if (!paramsChanged && !skipCalculation) {
      console.log('パラメータが変わっていないため、計算をスキップします');
      return;
    }

    // SVG内容が空の場合は計算しない（非表示レイヤーのみの場合など）
    if (!svgContent || svgContent.trim() === '') {
      console.log('SVG内容が空のため、計算をスキップします');
      setCoverage(0);
      return;
    }

    // 計算中なら再計算しない
    if (isCalculating) return;

    // SVG内容や寸法が無効な場合も計算しない
    if (width <= 0 || height <= 0) return;

    // 前回のタイマーがあればクリア
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // 新しいタイマーをセット
    timerRef.current = window.setTimeout(() => {
      calculateCoverageValue();
      // タイマーをクリア
      timerRef.current = null;
    }, updateRate);

    // クリーンアップ関数
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    svgContent,
    width,
    height,
    backgroundImage,
    threshold,
    isCalculating,
    updateRate,
    skipCalculation,
    layers, // レイヤー情報が変更されたときにも再計算（黒い色のレイヤーの有無をチェックするため）
  ]);

  // 色が黒っぽいかどうかをチェックする関数
  const isBlackishColor = (color: string): boolean => {
    // カラーコードから各色成分を取得
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);

    // 黒っぽい色の条件（RGB値が各50以下で合計150未満）
    return r <= 50 && g <= 50 && b <= 50 && r + g + b < 150;
  };

  // レイヤーに黒っぽい色が含まれているかチェック
  const hasBlackishLayers = (): boolean => {
    if (layers.length === 0) return true; // レイヤー情報がない場合は計算を続行

    return layers.some(
      (layer) =>
        layer.visible &&
        layer.svgPattern &&
        isBlackishColor(layer.parameters.color)
    );
  };

  // 隠蔽率を計算する関数
  const calculateCoverageValue = async () => {
    if (!backgroundImage || !svgContent) return;

    // レイヤーの色情報を文字列化（関数内でも取得）
    const layerColors = layers
      .filter((layer) => layer.visible && layer.svgPattern)
      .map((layer) => layer.parameters.color)
      .join(',');

    // 黒っぽいレイヤーがあるかチェック
    if (!hasBlackishLayers()) {
      console.log('黒っぽい色のレイヤーがないため、隠蔽率は0%とします');
      setCoverage(0);
      return;
    }

    setIsCalculating(true);
    try {
      // 背景画像との隠蔽率を計算
      console.log('背景画像との隠蔽率を計算します - 閾値:', threshold);
      const result = await calculateCoverageWithBackground(
        svgContent,
        backgroundImage,
        width,
        height,
        threshold
      );

      // 計算結果を設定
      setCoverage(parseFloat(result.coverage.toFixed(2)));

      // パラメータを保存
      lastParamsRef.current = {
        svgContent,
        width,
        height,
        backgroundImage,
        threshold,
        layerColors,
      };

      // 変化があった場合、スキップフラグをリセット
      if (result.changed) {
        setSkipCalculation(false);
      }
    } catch (error) {
      console.error('隠蔽率の計算に失敗しました:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  // 背景画像がない場合は表示しない
  if (!backgroundImage) {
    return (
      <div className="bg-white p-3 rounded-md shadow-sm">
        <p className="text-sm text-gray-500">
          背景画像がないため、隠蔽率は計算できません
        </p>
      </div>
    );
  }

  // SVG内容が空の場合（すべてのレイヤーが非表示など）
  if (!svgContent || svgContent.trim() === '') {
    return (
      <div className="bg-white p-3 rounded-md shadow-sm">
        <div className="flex items-center mb-2">
          <span className="text-sm font-medium text-gray-700 mr-2">
            Covering Ratio:
          </span>
          <span className="text-lg font-semibold">0%</span>
          <p className="text-xs text-gray-500 ml-2">
            （表示レイヤーがありません）
          </p>
        </div>
      </div>
    );
  }

  // 黒っぽい色のレイヤーがない場合のメッセージ
  if (layers.length > 0 && !hasBlackishLayers()) {
    return (
      <div className="bg-white p-3 rounded-md shadow-sm">
        <div className="flex items-center mb-2">
          <span className="text-sm font-medium text-gray-700 mr-2">
            Covering Ratio:
          </span>
          <span className="text-lg font-semibold">0%</span>
        </div>
        <p className="text-xs text-gray-500">
          （黒色レイヤーを追加してください）
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white p-3 rounded-md shadow-sm">
      <div className="flex items-center mb-2">
        <span className="text-sm font-medium text-gray-700 mr-2">
          Covering Ratio:
        </span>
        {isCalculating ? (
          <span className="text-lg font-semibold">Calculating...</span>
        ) : (
          <span className="text-lg font-semibold">{coverage}%</span>
        )}
        {isCalculating && (
          <div className="ml-2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        )}
      </div>
    </div>
  );
};

export default CoverageDisplay;
